/**
 * NotificationItem Component
 *
 * Individual notification item with icon, title, message, and time.
 * Supports read/unread states and click actions.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import {
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  Users,
  Bell,
  Gift,
  X,
  BookOpen,
  UserPlus,
  ArrowRightLeft,
  Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Notification, NotificationType } from "@/lib/api";

// ═══════════════════════════════════════════════════════════════════
// Icon & Color Mapping
// ═══════════════════════════════════════════════════════════════════

const NOTIFICATION_CONFIG: Record<
  NotificationType,
  {
    icon: typeof Bell;
    bgColor: string;
    iconColor: string;
  }
> = {
  subscription_renewed: {
    icon: CheckCircle2,
    bgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
  subscription_expiring: {
    icon: Clock,
    bgColor: "bg-amber-100",
    iconColor: "text-amber-600",
  },
  payment_received: {
    icon: CreditCard,
    bgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
  payment_failed: {
    icon: AlertCircle,
    bgColor: "bg-red-100",
    iconColor: "text-red-600",
  },
  payment_retry_scheduled: {
    icon: Clock,
    bgColor: "bg-orange-100",
    iconColor: "text-orange-600",
  },
  free_year_ending: {
    icon: Gift,
    bgColor: "bg-purple-100",
    iconColor: "text-purple-600",
  },
  report_exported: {
    icon: FileText,
    bgColor: "bg-sky-100",
    iconColor: "text-sky-600",
  },
  annual_report_created: {
    icon: FileText,
    bgColor: "bg-blue-100",
    iconColor: "text-blue-600",
  },
  annual_report_status_changed: {
    icon: FileText,
    bgColor: "bg-indigo-100",
    iconColor: "text-indigo-600",
  },
  annual_report_exported: {
    icon: FileText,
    bgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
  share_book_created: {
    icon: BookOpen,
    bgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
  share_book_exported: {
    icon: BookOpen,
    bgColor: "bg-sky-100",
    iconColor: "text-sky-600",
  },
  capital_change: {
    icon: ArrowRightLeft,
    bgColor: "bg-amber-100",
    iconColor: "text-amber-600",
  },
  shareholder_added: {
    icon: UserPlus,
    bgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
  shareholder_updated: {
    icon: Users,
    bgColor: "bg-blue-100",
    iconColor: "text-blue-600",
  },
  shareholders_imported: {
    icon: Users,
    bgColor: "bg-violet-100",
    iconColor: "text-violet-600",
  },
  pledge_added: {
    icon: Shield,
    bgColor: "bg-orange-100",
    iconColor: "text-orange-600",
  },
  pledge_removed: {
    icon: Shield,
    bgColor: "bg-gray-100",
    iconColor: "text-gray-600",
  },
  transaction_registered: {
    icon: ArrowRightLeft,
    bgColor: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
  company_invitation: {
    icon: Users,
    bgColor: "bg-indigo-100",
    iconColor: "text-indigo-600",
  },
  system_announcement: {
    icon: Bell,
    bgColor: "bg-gray-100",
    iconColor: "text-gray-600",
  },
};

// ═══════════════════════════════════════════════════════════════════
// URL Resolution (handles legacy notifications with incorrect URLs)
// ═══════════════════════════════════════════════════════════════════

function resolveActionUrl(notification: Notification): string | null {
  const { actionUrl, type, data } = notification;
  const companyId = data?.companyId as string | undefined;
  const reportId = data?.reportId as string | undefined;

  // Handle legacy URLs that need fixing
  if (actionUrl) {
    // Fix legacy sharebook URLs without /dashboard prefix
    if (actionUrl.match(/^\/companies\/[^/]+\/share-book/) || actionUrl.match(/^\/companies\/[^/]+\/sharebook/)) {
      const match = actionUrl.match(/^\/companies\/([^/]+)/);
      if (match) {
        return `/dashboard/companies/${match[1]}/share-book`;
      }
    }

    // Fix legacy sharebook URLs with wrong path (sharebook instead of share-book)
    if (actionUrl.match(/\/sharebook/)) {
      return actionUrl.replace(/\/sharebook(\/.*)?$/, "/share-book");
    }

    // Fix legacy report URLs: /companies/xxx/reports/yyy → /wizard/yyy
    const reportMatch = actionUrl.match(/\/companies\/[^/]+\/reports\/([a-f0-9-]+)/);
    if (reportMatch) {
      return `/dashboard/wizard/${reportMatch[1]}`;
    }

    // Fix other URLs missing /dashboard prefix
    if (actionUrl.match(/^\/companies\/[^/]+/) && !actionUrl.startsWith("/dashboard")) {
      return `/dashboard${actionUrl}`;
    }

    // If actionUrl is already a proper URL (not just /dashboard), use it
    if (actionUrl !== "/dashboard" && actionUrl !== "/dashboard/") {
      return actionUrl;
    }
  }

  // Legacy notifications with no actionUrl or just "/dashboard": derive URL from data
  if (companyId) {
    // Sharebook notifications - all go to share-book page
    if (type === "share_book_created" || type === "share_book_exported" || type === "capital_change" ||
        type === "shareholder_added" || type === "shareholder_updated" ||
        type === "shareholders_imported" || type === "pledge_added" || type === "pledge_removed" ||
        type === "transaction_registered") {
      return `/dashboard/companies/${companyId}/share-book`;
    }

    // Annual report notifications - go to wizard
    if ((type === "report_exported" || type === "annual_report_created" ||
         type === "annual_report_status_changed" || type === "annual_report_exported") && reportId) {
      return `/dashboard/wizard/${reportId}`;
    }

    // Default: go to company page
    return `/dashboard/companies/${companyId}`;
  }

  // Payment failed → billing
  if (type === "payment_failed") {
    return "/dashboard/billing";
  }

  // Fallback to original actionUrl (might be null or dashboard)
  return actionUrl || null;
}

// ═══════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  compact?: boolean;
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  compact = false,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const isUnread = !notification.readAt;
  const config = NOTIFICATION_CONFIG[notification.type] || NOTIFICATION_CONFIG.system_announcement;
  const Icon = config.icon;

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
    locale: sv,
  });

  const handleClick = useCallback(() => {
    // Mark as read if unread
    if (isUnread && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    // Navigate to resolved URL (handles legacy notifications)
    const targetUrl = resolveActionUrl(notification);
    if (targetUrl) {
      navigate(targetUrl);
    }
  }, [isUnread, onMarkAsRead, notification, navigate]);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(notification.id);
    },
    [onDelete, notification.id]
  );

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group relative flex items-start gap-3 px-4 transition-all duration-200 cursor-pointer",
        "hover:bg-gray-50/80",
        compact ? "py-3" : "py-4",
        // Left border indicator for unread
        isUnread && "border-l-[3px] border-l-primary-500 bg-primary-50/30"
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          "flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105",
          config.bgColor
        )}
      >
        <Icon className={cn("w-[18px] h-[18px]", config.iconColor)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pr-6">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "text-sm leading-snug",
              isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-600"
            )}
          >
            {notification.title}
          </p>
          <span className="flex-shrink-0 text-[11px] text-gray-400 mt-0.5">
            {timeAgo}
          </span>
        </div>
        {notification.message && (
          <p className={cn(
            "mt-1 text-[13px] leading-relaxed text-gray-500",
            compact ? "line-clamp-1" : "line-clamp-2"
          )}>
            {notification.message}
          </p>
        )}
      </div>

      {/* Delete button (shows on hover) */}
      {onDelete && (
        <button
          onClick={handleDelete}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 opacity-0 group-hover:opacity-100 hover:bg-gray-200 hover:text-gray-600 transition-all duration-150"
          aria-label="Ta bort notifikation"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
